// Standalone help doc for the "Studio Visual Mode" admin default (shorts.studio.visual_mode).
// Rendered via <iframe srcDoc={...}> in AdminDefaults.tsx — kept as a full document (not a
// fragment) since it has no host page to inherit <head>/theme wiring from in that context.
// See spec/shorts_settings.md §2 for the underlying STUDIO_VISUAL_MAX_* settings this documents.
export const STUDIO_VISUAL_QUALITY_GATE_HELP_HTML = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Studio Quality Gate</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap">
<style>
  :root{
    --bg:#f3f4f6;
    --surface:#ffffff;
    --surface-2:#eceef2;
    --border:#d7dce3;
    --text:#1a2028;
    --text-dim:#5b6472;
    --accent:#0e7fb0;
    --accent-soft:#dcedf5;
    --good:#1c8a58;
    --good-soft:#dcf3e7;
    --danger:#c73a2b;
    --danger-soft:#fbe4e0;
    --sans:'IBM Plex Sans',system-ui,-apple-system,sans-serif;
    --mono:'IBM Plex Mono',ui-monospace,'SFMono-Regular',Menlo,monospace;
  }
  @media (prefers-color-scheme: dark){
    :root:not([data-theme="light"]){
      --bg:#0d1117;
      --surface:#151b23;
      --surface-2:#1b222c;
      --border:#2a3340;
      --text:#e7ebf0;
      --text-dim:#8b95a4;
      --accent:#5cbce8;
      --accent-soft:#16303e;
      --good:#4fd18b;
      --good-soft:#123322;
      --danger:#ff8172;
      --danger-soft:#3a1a17;
    }
  }
  :root[data-theme="dark"]{
    --bg:#0d1117;
    --surface:#151b23;
    --surface-2:#1b222c;
    --border:#2a3340;
    --text:#e7ebf0;
    --text-dim:#8b95a4;
    --accent:#5cbce8;
    --accent-soft:#16303e;
    --good:#4fd18b;
    --good-soft:#123322;
    --danger:#ff8172;
    --danger-soft:#3a1a17;
  }

  *{box-sizing:border-box;}
  body{
    margin:0;
    background:var(--bg);
    color:var(--text);
    font-family:var(--sans);
    padding:0 20px;
  }
  main{
    max-width:880px;
    margin:0 auto;
    padding-block:32px 48px;
    display:flex;
    flex-direction:column;
    gap:36px;
  }

  .eyebrow{
    font-family:var(--mono);
    font-size:11.5px;
    letter-spacing:.09em;
    text-transform:uppercase;
    color:var(--accent);
    margin:0 0 10px;
  }
  h1{
    font-size:clamp(22px,4vw,30px);
    line-height:1.15;
    margin:0 0 14px;
    text-wrap:balance;
    font-weight:700;
  }
  .lede{
    font-size:15px;
    line-height:1.65;
    color:var(--text-dim);
    max-width:65ch;
    margin:0;
  }
  .lede code, .inline-code{
    font-family:var(--mono);
    font-size:.92em;
    background:var(--surface-2);
    border:1px solid var(--border);
    border-radius:4px;
    padding:.05em .35em;
    color:var(--text);
  }

  figure{margin:0;color:var(--text);}
  .diagram-frame{
    background:var(--surface);
    border:1px solid var(--border);
    border-radius:12px;
    padding:18px 14px 8px;
    overflow-x:auto;
  }
  svg.diagram{display:block;width:100%;height:auto;min-width:640px;}
  figcaption{
    margin-top:12px;
    font-size:13.5px;
    color:var(--text-dim);
    line-height:1.55;
  }
  .legend{
    display:flex;
    gap:20px;
    flex-wrap:wrap;
    margin-top:14px;
    font-family:var(--mono);
    font-size:11.5px;
    color:var(--text-dim);
  }
  .legend span{display:inline-flex;align-items:center;gap:6px;}
  .legend i{width:16px;height:2px;display:inline-block;background:var(--text-dim);}
  .legend i.dashed{background:none;border-top:2px dashed var(--accent);}
  .legend i.solid{background:var(--text-dim);}

  h2{
    font-size:12px;
    font-family:var(--mono);
    text-transform:uppercase;
    letter-spacing:.08em;
    color:var(--text-dim);
    margin:0 0 16px;
    padding-bottom:10px;
    border-bottom:1px solid var(--border);
  }

  .grid{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:14px;
  }
  @media (max-width:620px){ .grid{grid-template-columns:1fr;} }

  .card{
    background:var(--surface);
    border:1px solid var(--border);
    border-radius:10px;
    padding:18px 18px 16px;
    display:flex;
    flex-direction:column;
    gap:10px;
  }
  .card-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:10px;
  }
  .card-title{
    font-size:15px;
    font-weight:600;
  }
  .card-sub{
    font-family:var(--mono);
    font-size:11px;
    color:var(--text-dim);
    margin-top:2px;
    word-break:break-all;
  }
  .glyph{flex:none;color:var(--accent);}
  .card p{
    margin:0;
    font-size:13.5px;
    line-height:1.55;
    color:var(--text-dim);
  }
  .reject-msg{
    font-family:var(--mono);
    font-size:11.5px;
    background:var(--danger-soft);
    color:var(--danger);
    border-radius:6px;
    padding:7px 9px;
    line-height:1.5;
  }
  .badge{
    align-self:flex-start;
    font-family:var(--mono);
    font-size:10.5px;
    letter-spacing:.02em;
    padding:3px 8px;
    border-radius:999px;
    background:var(--good-soft);
    color:var(--good);
  }
  .badge.off{
    background:var(--surface-2);
    color:var(--text-dim);
  }

  table{
    width:100%;
    border-collapse:collapse;
    font-size:13px;
    background:var(--surface);
    border:1px solid var(--border);
    border-radius:10px;
    overflow:hidden;
  }
  .table-wrap{overflow-x:auto;border-radius:10px;}
  th,td{
    text-align:left;
    padding:11px 14px;
    border-bottom:1px solid var(--border);
    vertical-align:top;
  }
  thead th{
    font-family:var(--mono);
    font-size:10.5px;
    text-transform:uppercase;
    letter-spacing:.06em;
    color:var(--text-dim);
    background:var(--surface-2);
  }
  tbody tr:last-child td{border-bottom:none;}
  td.var{font-family:var(--mono);font-size:12px;color:var(--accent);}
  td.default{font-family:var(--mono);font-size:12px;color:var(--text-dim);}

  footer{
    font-size:12.5px;
    color:var(--text-dim);
    line-height:1.6;
    border-top:1px solid var(--border);
    padding-top:18px;
  }
  footer code{font-family:var(--mono);color:var(--text);}

  .plain{
    background:var(--accent-soft);
    border:1px solid var(--border);
    border-radius:10px;
    padding:18px 20px;
  }
  .plain h2{
    border-bottom:none;
    padding-bottom:0;
    margin-bottom:10px;
    color:var(--accent);
  }
  .plain > p{
    margin:0 0 14px;
    font-size:13.5px;
    line-height:1.6;
    color:var(--text);
    max-width:65ch;
  }
  .plain dl{
    margin:0;
    display:flex;
    flex-direction:column;
    gap:9px;
  }
  .plain dl div{display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;}
  .plain dt{
    font-family:var(--mono);
    font-size:11.5px;
    font-weight:600;
    color:var(--accent);
    flex:none;
    min-width:118px;
  }
  .plain dd{
    margin:0;
    font-size:13.5px;
    color:var(--text);
    line-height:1.5;
  }
  .plain .ceiling{
    margin:14px 0 0;
    font-size:13px;
    color:var(--text-dim);
    border-top:1px dashed var(--border);
    padding-top:12px;
  }
</style>
</head>
<body>
<main>
  <header>
    <p class="eyebrow">media-pipeline · src/studio/select</p>
    <h1>The visual quality gate</h1>
    <p class="lede">
      Before a candidate clip can be scored for a short, Studio v2 samples its
      highlight window with ffmpeg and reduces it to four numbers. Any of them
      crossing an optional <code>STUDIO_VISUAL_MAX_*</code> cap gets the clip
      rejected outright; two of the same numbers also quietly shave points off
      clips that stay under the cap.
    </p>
  </header>

  <section class="plain">
    <h2>In plain terms</h2>
    <p>
      Studio auto-picks clips from the source video. Some clips are bad
      quality — frozen, blown-out, blurry, or flickery. These four settings
      are how much of that you'll tolerate before a clip gets thrown out and
      a different one picked instead.
    </p>
    <dl>
      <div><dt>FREEZE_RATIO</dt><dd>too much of the clip is a paused / stuck frame</dd></div>
      <div><dt>CLIP_RATIO</dt><dd>too much of the clip is a solid black or blown-out white screen</dd></div>
      <div><dt>BLUR_MEAN</dt><dd>the clip is blurry on average</dd></div>
      <div><dt>FLICKER</dt><dd>the brightness jumps around frame to frame (strobing)</dd></div>
    </dl>
    <p class="ceiling">
      Each number is a <strong>ceiling</strong>: if the clip's measured value goes above it, the clip is rejected. Leave a setting blank and there's no ceiling for that check — nothing gets rejected for it.
    </p>
    <p class="ceiling" style="border-top:none;padding-top:0;margin-top:10px;">
      None of this rejects anything unless <code class="inline-code">STUDIO_VISUAL=gate</code> is also set (the dropdown next to this help icon). There's a separate on/off switch for the whole system:
    </p>
    <dl>
      <div><dt>off</dt><dd>no checks at all — the four numbers aren't even measured</dd></div>
      <div><dt>shadow <span style="color:var(--text-dim);font-weight:400;">(default)</span></dt><dd>numbers are measured and saved, but nothing is ever rejected because of them</dd></div>
      <div><dt>gate</dt><dd>the four ceilings above are actually enforced — a clip over any cap gets thrown out</dd></div>
    </dl>
  </section>

  <section>
    <figure>
      <div class="diagram-frame">
        <svg class="diagram" viewBox="0 0 960 500" role="img" aria-label="A clip window is measured into four signals — freeze ratio, clip ratio, blur mean, and flicker. All four feed the QualityGate, which rejects the clip if any exceeds its STUDIO_VISUAL_MAX cap, otherwise keeps it. Freeze ratio and clip ratio additionally feed the Scorer as a continuous penalty, applied even when under the cap.">
          <defs>
            <marker id="arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L8,4 L0,8 Z" fill="currentColor"/>
            </marker>
            <marker id="arrowAccent" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L8,4 L0,8 Z" style="fill:var(--accent)"/>
            </marker>
          </defs>

          <rect x="20" y="140" width="120" height="130" rx="8" fill="none" stroke="currentColor" stroke-width="1.4"/>
          <text x="80" y="198" text-anchor="middle" font-family="IBM Plex Mono" font-size="11.5" font-weight="600" fill="currentColor">CLIP</text>
          <text x="80" y="212" text-anchor="middle" font-family="IBM Plex Mono" font-size="11.5" font-weight="600" fill="currentColor">WINDOW</text>
          <text x="80" y="232" text-anchor="middle" font-family="IBM Plex Sans" font-size="10.5" fill="var(--text-dim)">sampled frames</text>

          <path d="M140,205 C175,205 175,66 210,66" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>
          <path d="M140,205 C175,205 175,156 210,156" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>
          <path d="M140,205 C175,205 175,246 210,246" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>
          <path d="M140,205 C175,205 175,336 210,336" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>

          <g font-family="IBM Plex Mono">
            <rect x="210" y="32" width="180" height="68" rx="7" fill="var(--surface-2)" stroke="var(--border)"/>
            <text x="224" y="60" font-size="12.5" font-weight="600" fill="currentColor">freezeRatio</text>
            <text x="224" y="78" font-size="10" fill="var(--text-dim)">% of window frozen</text>

            <rect x="210" y="122" width="180" height="68" rx="7" fill="var(--surface-2)" stroke="var(--border)"/>
            <text x="224" y="150" font-size="12.5" font-weight="600" fill="currentColor">clipRatio</text>
            <text x="224" y="168" font-size="10" fill="var(--text-dim)">% blown out</text>

            <rect x="210" y="212" width="180" height="68" rx="7" fill="var(--surface-2)" stroke="var(--border)"/>
            <text x="224" y="240" font-size="12.5" font-weight="600" fill="currentColor">blurMean</text>
            <text x="224" y="258" font-size="10" fill="var(--text-dim)">lavfi.blur avg</text>

            <rect x="210" y="302" width="180" height="68" rx="7" fill="var(--surface-2)" stroke="var(--border)"/>
            <text x="224" y="330" font-size="12.5" font-weight="600" fill="currentColor">flicker</text>
            <text x="224" y="348" font-size="10" fill="var(--text-dim)">Δ luma stddev</text>
          </g>

          <path d="M390,66 L580,130" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>
          <path d="M390,156 L580,177" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>
          <path d="M390,246 L580,223" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>
          <path d="M390,336 L580,270" fill="none" stroke="currentColor" stroke-width="1.2" marker-end="url(#arrow)"/>
          <text x="480" y="95" text-anchor="middle" font-family="IBM Plex Mono" font-size="10" fill="var(--text-dim)">&gt; STUDIO_VISUAL_MAX_*</text>

          <circle cx="450" cy="86" r="3" style="fill:var(--accent)"/>
          <circle cx="470" cy="165" r="3" style="fill:var(--accent)"/>
          <path d="M450,86 L450,420" fill="none" style="stroke:var(--accent)" stroke-width="1.3" stroke-dasharray="4 4" marker-end="url(#arrowAccent)"/>
          <path d="M470,165 L470,420" fill="none" style="stroke:var(--accent)" stroke-width="1.3" stroke-dasharray="4 4" marker-end="url(#arrowAccent)"/>

          <rect x="580" y="80" width="170" height="240" rx="9" fill="var(--surface)" stroke="currentColor" stroke-width="1.5"/>
          <text x="665" y="112" text-anchor="middle" font-family="IBM Plex Mono" font-size="13" font-weight="700" fill="currentColor">QUALITY GATE</text>
          <text x="665" y="130" text-anchor="middle" font-family="IBM Plex Sans" font-size="10" fill="var(--text-dim)">QualityGate.ts</text>
          <line x1="600" y1="145" x2="730" y2="145" stroke="var(--border)"/>
          <text x="665" y="230" text-anchor="middle" font-family="IBM Plex Sans" font-size="11" fill="var(--text-dim)">reject if any</text>
          <text x="665" y="246" text-anchor="middle" font-family="IBM Plex Sans" font-size="11" fill="var(--text-dim)">signal exceeds</text>
          <text x="665" y="262" text-anchor="middle" font-family="IBM Plex Sans" font-size="11" fill="var(--text-dim)">its cap</text>

          <path d="M750,140 L800,140" fill="none" style="stroke:var(--good)" stroke-width="1.4" marker-end="url(#arrow)"/>
          <path d="M750,260 C780,260 780,285 800,285" fill="none" style="stroke:var(--danger)" stroke-width="1.4" marker-end="url(#arrow)"/>
          <text x="775" y="130" text-anchor="middle" font-family="IBM Plex Mono" font-size="9.5" style="fill:var(--good)">under all caps</text>
          <text x="775" y="300" text-anchor="middle" font-family="IBM Plex Mono" font-size="9.5" style="fill:var(--danger)">over any cap</text>

          <rect x="800" y="110" width="140" height="60" rx="8" fill="var(--good-soft)" stroke="var(--good)"/>
          <text x="870" y="136" text-anchor="middle" font-family="IBM Plex Mono" font-size="12" font-weight="600" style="fill:var(--good)">KEPT</text>
          <text x="870" y="153" text-anchor="middle" font-family="IBM Plex Sans" font-size="9.5" style="fill:var(--good)">goes to Scorer</text>

          <rect x="800" y="250" width="140" height="70" rx="8" fill="var(--danger-soft)" stroke="var(--danger)"/>
          <text x="870" y="278" text-anchor="middle" font-family="IBM Plex Mono" font-size="12" font-weight="600" style="fill:var(--danger)">REJECTED</text>
          <text x="870" y="294" text-anchor="middle" font-family="IBM Plex Sans" font-size="9" style="fill:var(--danger)">"visual: frozen 41%</text>
          <text x="870" y="306" text-anchor="middle" font-family="IBM Plex Sans" font-size="9" style="fill:var(--danger)">of window"</text>

          <rect x="210" y="420" width="390" height="60" rx="8" fill="var(--surface-2)" stroke="var(--border)"/>
          <text x="230" y="443" font-family="IBM Plex Mono" font-size="12" font-weight="600" fill="currentColor">SCORER</text>
          <text x="230" y="459" font-family="IBM Plex Sans" font-size="10" fill="var(--text-dim)">Scorer.ts — bonus -= min(k, ratio × n), always applied</text>
        </svg>
      </div>
      <figcaption>
        All four signals can fail a clip outright. Only <span class="inline-code" style="padding:.02em .3em;">freezeRatio</span> and <span class="inline-code" style="padding:.02em .3em;">clipRatio</span> also feed a continuous penalty in the ranking score — so a clip can be mildly frozen, stay under the cap, and still lose ranking points.
      </figcaption>
      <div class="legend">
        <span><i class="solid"></i> hard gate (reject)</span>
        <span><i class="dashed"></i> soft penalty (score only)</span>
      </div>
    </figure>
  </section>

  <section>
    <h2>The four signals</h2>
    <div class="grid">

      <div class="card">
        <div class="card-head">
          <div>
            <div class="card-title">Freeze ratio</div>
            <div class="card-sub">STUDIO_VISUAL_MAX_FREEZE_RATIO</div>
          </div>
          <svg class="glyph" width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
            <rect x="3" y="3" width="26" height="26" rx="5" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <line x1="12" y1="10" x2="12" y2="22" stroke="currentColor" stroke-width="2.4"/>
            <line x1="20" y1="10" x2="20" y2="22" stroke="currentColor" stroke-width="2.4"/>
          </svg>
        </div>
        <p>Fraction of the window sitting inside an ffmpeg <span class="inline-code">freezedetect</span> event — a stuck or near-static frame held too long.</p>
        <span class="badge">also scored by Scorer.ts</span>
        <div class="reject-msg">"visual: frozen 41% of window"</div>
      </div>

      <div class="card">
        <div class="card-head">
          <div>
            <div class="card-title">Clip ratio</div>
            <div class="card-sub">STUDIO_VISUAL_MAX_CLIP_RATIO</div>
          </div>
          <svg class="glyph" width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
            <rect x="4" y="20" width="4" height="8" fill="currentColor"/>
            <rect x="10" y="4" width="4" height="24" fill="currentColor"/>
            <rect x="16" y="4" width="4" height="24" fill="currentColor"/>
            <rect x="22" y="14" width="4" height="14" fill="currentColor"/>
          </svg>
        </div>
        <p>Fraction of frames near-uniformly black or blown-out white — luma average ≤8 or ≥247.</p>
        <span class="badge">also scored by Scorer.ts</span>
        <div class="reject-msg">"visual: clipped/blown-out 30% of window"</div>
      </div>

      <div class="card">
        <div class="card-head">
          <div>
            <div class="card-title">Blur mean</div>
            <div class="card-sub">STUDIO_VISUAL_MAX_BLUR_MEAN</div>
          </div>
          <svg class="glyph" width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
            <circle cx="16" cy="16" r="11" fill="none" stroke="currentColor" stroke-width="1.4" opacity=".35"/>
            <circle cx="16" cy="16" r="7.5" fill="none" stroke="currentColor" stroke-width="1.4" opacity=".6"/>
            <circle cx="16" cy="16" r="4" fill="currentColor" opacity=".9"/>
          </svg>
        </div>
        <p>Mean <span class="inline-code">lavfi.blur</span> score across the window — higher means blurrier. Gate-only, not scored.</p>
        <span class="badge off">gate only</span>
        <div class="reject-msg">"visual: blur 6.80 > 5.00"</div>
      </div>

      <div class="card">
        <div class="card-head">
          <div>
            <div class="card-title">Flicker</div>
            <div class="card-sub">STUDIO_VISUAL_MAX_FLICKER</div>
          </div>
          <svg class="glyph" width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
            <polyline points="3,18 9,10 13,22 17,8 21,20 25,12 29,17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"/>
          </svg>
        </div>
        <p>Standard deviation of frame-to-frame luma delta — an exposure-flicker / strobing proxy. Gate-only, not scored.</p>
        <span class="badge off">gate only</span>
        <div class="reject-msg">"visual: flicker 14.2 > 10.0"</div>
      </div>

    </div>
  </section>

  <section>
    <h2>Reference</h2>
    <div class="table-wrap">
      <table>
        <thead>
          <tr><th>Env var</th><th>Default</th><th>Rejects when</th></tr>
        </thead>
        <tbody>
          <tr><td class="var">STUDIO_VISUAL</td><td class="default">shadow</td><td>set to <span class="inline-code" style="padding:.02em .3em;">off</span>/<span class="inline-code" style="padding:.02em .3em;">shadow</span>/<span class="inline-code" style="padding:.02em .3em;">gate</span> — must be <strong>gate</strong> for any cap below to actually reject</td></tr>
          <tr><td class="var">STUDIO_VISUAL_MAX_FREEZE_RATIO</td><td class="default">unset = uncapped</td><td>freezeRatio exceeds it</td></tr>
          <tr><td class="var">STUDIO_VISUAL_MAX_CLIP_RATIO</td><td class="default">unset = uncapped</td><td>clipRatio exceeds it</td></tr>
          <tr><td class="var">STUDIO_VISUAL_MAX_BLUR_MEAN</td><td class="default">unset = uncapped</td><td>blurMean exceeds it</td></tr>
          <tr><td class="var">STUDIO_VISUAL_MAX_FLICKER</td><td class="default">unset = uncapped</td><td>flicker exceeds it</td></tr>
        </tbody>
      </table>
    </div>
  </section>

  <footer>
    Read via <code>envFloatOrNull()</code> in <code>studio/index.ts</code> — each defaults to <code>null</code> (no cap) if unset. Signals computed in <code>studio/signals/QualitySignal.ts</code>, enforced in <code>studio/select/QualityGate.ts</code>, two of them also weighted in <code>studio/select/Scorer.ts</code>.
  </footer>
</main>
</body>
</html>
`;
